import {useMemo} from 'react';
import type {BreadcrumbAtomPropTypes} from '@sdb/ui/types/components/atoms/Breadcrumb.types';
import type {
    NavigationRailMenuDataItem,
    NavigationRailMenuDataTierOneItem,
} from '@sdb/ui/types/components/molecules/NavigationRail/NavigationRail.types';

export const useBreadcrumbs = (
    navData: NavigationRailMenuDataItem[],
    selectedId: string | undefined,
) => {
    return useMemo(() => {
        const rslt = [] as BreadcrumbAtomPropTypes[];

        if (navData && navData.length) {
            const findPath = (
                items:
                    | (NavigationRailMenuDataItem | NavigationRailMenuDataTierOneItem)[]
                    | undefined,
                targetId: string | undefined,
            ): (NavigationRailMenuDataItem | NavigationRailMenuDataTierOneItem)[] | null => {
                if (!items || !targetId) return null;
                for (const it of items) {
                    if (it.id === targetId) return [it];
                    if (it.submenu && it.submenu.length) {
                        const sub = findPath(it.submenu, targetId);
                        if (sub) return [it, ...sub];
                    }
                }
                return null;
            };

            navData.forEach((value, index) => {
                const childPath = findPath(value.submenu, selectedId);

                if (index === 0 || value.id === selectedId || childPath) {
                    const path = value.href || undefined;

                    rslt.push({label: value.label?.toString() ?? '', path: path});
                }

                if (childPath) {
                    childPath.forEach((child) =>
                        rslt.push({
                            label: child?.label?.toString() ?? '',
                            path: child.href || undefined,
                        }),
                    );
                }
            });
        }

        // If we're on the dashboard (root path), only show the first breadcrumb
        // Check if the selected item is the root/dashboard by checking if it has href === '/'
        const findSelectedItem = (
            items: NavigationRailMenuDataItem[],
        ): NavigationRailMenuDataItem | null => {
            for (const item of items) {
                if (item.id === selectedId) return item;
                if (item.submenu) {
                    const found = findSelectedItem(item.submenu as NavigationRailMenuDataItem[]);
                    if (found) return found;
                }
            }
            return null;
        };

        const selectedItem = findSelectedItem(navData);

        if (selectedItem && selectedItem.href === '/' && rslt.length > 0) {
            // On the dashboard, only show the first breadcrumb (Start/Dashboard)
            return [rslt[0]];
        }

        return rslt;
    }, [navData, selectedId]);
};
